/**
 * PlayerReportsIdentityHeaderContainer — derives the reports screen's subject
 * (SE-7237).
 *
 * Reads the player the reports screen has ALREADY fetched: the screen's
 * container runs `usePlayerDetailQuery` to title the PDF, so the name, club and
 * positions are paid for and were simply never shown. No query is added here.
 *
 * **Nothing renders until the player exists.** A header with a blank name over
 * three aggregate cards is worse than no header — it reads as a player whose
 * name failed to load rather than as a screen still loading — so an absent or
 * in-flight player renders null and the aggregates stand alone for the moment
 * they take to arrive.
 * @module features/reports/components/organisms/PlayerReportsIdentityHeader/PlayerReportsIdentityHeaderContainer
 */
import type { Href } from "expo-router";
import React, { memo, useMemo } from "react";

import { EMPTY_VALUE_PLACEHOLDER } from "@/features/player-detail/utils/playerPageHeaderConstants";
import { getPlayerPositions } from "@/features/player-detail/utils/playerPageHeaderUtils";
import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";

import PlayerReportsIdentityHeaderView from "./PlayerReportsIdentityHeaderView";

/** Props for PlayerReportsIdentityHeaderContainer. */
export interface PlayerReportsIdentityHeaderContainerProps {
  /** The player whose reports these are, or null/undefined while loading. */
  readonly player: PlayerDetailPlayerMetaFragment | null | undefined;
  /** The route's player id, used to build the way back. */
  readonly playerId: string | undefined;
}

/**
 * Derives the identity line and the route back to the player page.
 * @param root0 - Component props
 * @param root0.player - The player whose reports these are
 * @param root0.playerId - The route's player id
 * @returns The header element, or null while there is no player to name.
 */
const PlayerReportsIdentityHeaderContainer = ({
  player,
  playerId,
}: PlayerReportsIdentityHeaderContainerProps) => {
  // Context-expanded, because this same screen is mounted under `/search` as
  // well as under `/players`; a bare `/players/{id}` would jump the reader out
  // of the search stack they navigated in through.
  const playerHref = useExpandedPlayerUrl(`/players/${playerId ?? ""}`);

  const subtitle = useMemo(() => {
    // Same precedence as the player page's own header, so the two never
    // disagree about which club a loaned or newly-transferred player is at.
    const clubName = player?.team?.name ?? player?.teamName ?? null;
    const positions = getPlayerPositions(player);

    return (
      [clubName, positions === EMPTY_VALUE_PLACEHOLDER ? null : positions]
        .filter((part): part is string => part !== null && part.length > 0)
        .join(" • ") || null
    );
  }, [player]);

  if (!player) return null;

  return (
    <PlayerReportsIdentityHeaderView
      playerName={player.name}
      subtitle={subtitle}
      playerHref={playerHref as Href}
    />
  );
};

PlayerReportsIdentityHeaderContainer.displayName =
  "PlayerReportsIdentityHeaderContainer";

export default memo(PlayerReportsIdentityHeaderContainer);
